// ============================================================
// Contrack AI — Onboarding orchestrator (first-login setup flow)
// Faithful port of the Claude Design prototype's app.jsx orchestrator:
//   step 0 cinematic intro → steps 1–4 → step 5 building screen →
//   focus-pull handoff into the app's OWN existing dashboard.
//
// The prototype's step-6 dashboard is intentionally NOT recreated — the
// build's closing scale/fade hands off to our real dashboard instead
// (see onComplete + `.cob-dash-enter` in onboarding/onboarding.css).
//
// Step 4 connections use the app's REAL auth hooks (Gmail/Outlook OAuth);
// progress is saved to sessionStorage so the flow RESUMES at step 4 after
// the OAuth round-trip. Tripletex has no OAuth redirect (admin token-form
// in Settings → ERP), so its card stays a visual toggle + TODO.
// ============================================================
import { useState, useEffect, useRef, useMemo } from "react";
import api from "../lib/api";
import "./onboarding/onboarding.css";
import IntroSequence from "./onboarding/IntroSequence";
import { TopBar, FooterNav, StepCountry, StepContractType, Step1, Step2, Step3, Step4 } from "./onboarding/Steps";
import BuildingScreen from "./onboarding/BuildingScreen";
import type { OnboardingAnswers } from "./onboarding/data";
import { useOnboardingLang } from "./onboarding/i18n";

const Q_STEPS = 6; // number of question steps for the progress bar (country, type, industry, goals, volume, connection)

// sessionStorage key used to carry onboarding progress across the OAuth
// redirect (Gmail/Outlook leave the page and return via ?email_connected).
const RESUME_KEY = "cob_onboarding_resume";

type ResumeState = {
  country: string | null;
  contractType: string | null;
  industry: string | null;
  otherText: string;
  goals: string[];
  invoices: string | null;
  projects: string | null;
  emailSim: boolean;
  erpSim: boolean;
};

export default function OnboardingView({ onComplete }: {
  onComplete: (choice: { country: string | null; contractType: string | null }) => void;
}) {
  // Did we just return from an OAuth round-trip? If so, restore progress.
  const resume = useMemo<ResumeState | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const isOAuthReturn = params.has("email_connected") || params.has("email_error");
    if (!isOAuthReturn) return null;
    try {
      const raw = sessionStorage.getItem(RESUME_KEY);
      return raw ? (JSON.parse(raw) as ResumeState) : null;
    } catch { return null; }
  }, []);

  const [step, setStep] = useState(resume ? 6 : 1);          // 1-6 questions (resume → connection step 6)
  const [display, setDisplay] = useState(resume ? 6 : 0);    // 0 intro, 1-6 questions, 7 building
  const [phase, setPhase] = useState<"in" | "out" | "settled">("in"); // entrance/exit
  const [dir, setDir] = useState<"fwd" | "back">("fwd");
  const [fx, setFx] = useState<"slide" | "zoom">("slide");
  const [pulse, setPulse] = useState(false);                 // button "wake up" pulse

  // answers
  const [country, setCountry] = useState<string | null>(resume?.country ?? null);
  const [contractType, setContractType] = useState<string | null>(resume?.contractType ?? null);
  const [industry, setIndustry] = useState<string | null>(resume?.industry ?? null);
  const [otherText, setOtherText] = useState(resume?.otherText ?? "");
  const [goals, setGoals] = useState<string[]>(resume?.goals ?? []);
  const [invoices, setInvoices] = useState<string | null>(resume?.invoices ?? null);
  const [projects, setProjects] = useState<string | null>(resume?.projects ?? null);
  // connection state: "Sim" = visual toggle (demo); "Real" = live status from backend
  const [emailSim, setEmailSim] = useState(resume?.emailSim ?? false);
  const [erpSim, setErpSim] = useState(resume?.erpSim ?? false);
  const [emailReal, setEmailReal] = useState(false);
  const [erpReal, setErpReal] = useState(false);

  const { copy } = useOnboardingLang();

  const email = emailReal || emailSim;
  const erp = erpReal || erpSim;

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevEnabled = useRef(false);

  // ---- On mount: clean up an OAuth return, and read live connection status ----
  useEffect(() => {
    if (resume) {
      // strip ?email_connected/?email_error so a later refresh restarts fresh,
      // and drop the saved progress now that it's been restored into state.
      window.history.replaceState({}, "", window.location.pathname);
      sessionStorage.removeItem(RESUME_KEY);
    } else {
      // fresh start — clear any stale progress from an abandoned OAuth redirect.
      try { sessionStorage.removeItem(RESUME_KEY); } catch {}
    }
    let cancelled = false;
    (async () => {
      const [em, er] = await Promise.allSettled([
        api.get("/auth/email/status"),
        api.get("/erp/status"),
      ]);
      if (cancelled) return;
      if (em.status === "fulfilled") {
        const conns: any[] = em.value.data?.connections || [];
        setEmailReal(conns.some((c) => c?.status !== "disconnected"));
      }
      if (er.status === "fulfilled") {
        setErpReal(!!er.value.data?.connection?.connected);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Settle: once an entrance has had time to play, drop the animation class so
  // the resting state is plain visible CSS — robust even if the animation clock
  // is throttled (backgrounded tab, reduced-motion).
  useEffect(() => {
    if (phase === "in") {
      const t = setTimeout(() => setPhase("settled"), 620);
      return () => clearTimeout(t);
    }
  }, [phase, display]);

  const animClass =
    phase === "settled" ? "settled" : "anim-" + phase + "-" + (fx === "zoom" ? "zoom" : dir);

  function go(target: number, direction: "fwd" | "back", effect?: "slide" | "zoom") {
    if (target === display) return;
    setDir(direction);
    setFx(effect || "slide");
    setPhase("out");
    if (timer.current) clearTimeout(timer.current);
    const delay = effect === "zoom" ? 340 : 230;
    timer.current = setTimeout(() => {
      setDisplay(target);
      if (target >= 1 && target <= 6) setStep(target);
      setPhase("in");
    }, delay);
  }

  // Building complete → focus-pull (scale 0.98 + fade), then hand off to the
  // app's real dashboard. This is the "behold denne effekten" transition.
  function finish() {
    setDir("fwd");
    setFx("zoom");
    setPhase("out");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onComplete({ country, contractType }), 340);
  }

  const next = () => go(Math.min(display + 1, 6), "fwd");
  const back = () => go(Math.max(display - 1, 1), "back");

  function toggleGoal(id: string) {
    setGoals((g) => {
      if (g.includes(id)) return g.filter((x) => x !== id);
      if (g.length >= 2) return g;
      return [...g, id];
    });
  }

  // Step 4 — start a REAL OAuth connect, persisting progress for the round-trip.
  function connectProvider(provider: "gmail" | "outlook") {
    const state: ResumeState = { country, contractType, industry, otherText, goals, invoices, projects, emailSim, erpSim };
    try { sessionStorage.setItem(RESUME_KEY, JSON.stringify(state)); } catch {}
    api.get(`/auth/email/${provider}/connect`)
      .then((res) => { window.location.href = res.data.auth_url; })
      .catch(() => {
        // OAuth not configured / failed (e.g. bare demo env) — fall back to the
        // simulated "Tilkoblet" state so the cinematic flow continues uninterrupted.
        try { sessionStorage.removeItem(RESUME_KEY); } catch {}
        setEmailSim(true);
      });
  }

  const answers: OnboardingAnswers = { country, contractType, industry, goals, projects, invoices, email, erp };

  // ---- per-step gating (1 land, 2 type, 3 industry, 4 mål, 5 volum, 6 tilkobling) ----
  let nextEnabled = true;
  let nextLabel = copy.continueLabel;
  if (display === 1) nextEnabled = !!country;
  if (display === 2) nextEnabled = !!contractType;
  if (display === 3) nextEnabled = !!industry && (industry !== "annet" || otherText.trim().length > 0);
  if (display === 4) nextEnabled = goals.length >= 1;
  if (display === 5) nextEnabled = true; // volum skippable
  if (display === 6) { nextLabel = copy.finishSetup; nextEnabled = email || erp; }

  const showBack = display >= 2 && display <= 6;
  const onSkip = display === 5 ? () => go(6, "fwd") : null;

  // Button "wake up": when a step first becomes valid, pulse the primary button.
  useEffect(() => {
    if (display >= 1 && display <= 6) {
      if (nextEnabled && !prevEnabled.current) {
        prevEnabled.current = true;
        setPulse(true);
        const t = setTimeout(() => setPulse(false), 520);
        return () => clearTimeout(t);
      }
      if (!nextEnabled) prevEnabled.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextEnabled, display]);

  // ---- Step 0: cinematic intro ----
  if (display === 0) {
    return (
      <div className="cob-root">
        <IntroSequence onDone={() => go(1, "fwd")} />
      </div>
    );
  }

  // ---- Step 7: building screen (full-screen) ----
  if (display === 7) {
    return (
      <div className="cob-root">
        <div className={"screen full-screen fx-wrap " + animClass}>
          <BuildingScreen answers={answers} onDone={finish} />
        </div>
      </div>
    );
  }

  // ---- Steps 1–6 (1 land, 2 type, 3 industry, 4 mål, 5 volum, 6 tilkobling) ----
  return (
    <div className="cob-root">
      <div className="screen">
        <TopBar step={step} totalSteps={Q_STEPS} />
        <div className="stage">
          <div className={"stage-inner " + animClass} key={display}>
            {display === 1 && <StepCountry value={country} onChange={(c) => { setCountry(c); setContractType(null); }} />}
            {display === 2 && <StepContractType country={country} value={contractType} onChange={setContractType} />}
            {display === 3 && (
              <Step1 value={industry} otherText={otherText} onChange={setIndustry} onOtherText={setOtherText} />
            )}
            {display === 4 && <Step2 value={goals} onToggle={toggleGoal} />}
            {display === 5 && (
              <Step3 invoices={invoices} projects={projects} onInvoices={setInvoices} onProjects={setProjects} />
            )}
            {display === 6 && (
              <>
                <Step4
                  email={email}
                  erp={erp}
                  onToggleEmail={() => setEmailSim((v) => !v)}
                  onToggleErp={() => setErpSim((v) => !v)}
                  onConnectProvider={connectProvider}
                />
                <div className="step4-skip">
                  <button className="btn-link" type="button" onClick={() => go(7, "fwd")}>
                    {copy.step4Skip}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <FooterNav
          onBack={back}
          onNext={display === 6 ? () => go(7, "fwd") : next}
          nextLabel={nextLabel}
          nextEnabled={nextEnabled}
          pulse={pulse}
          showBack={showBack}
          onSkip={onSkip}
        />
      </div>
    </div>
  );
}
